/**
 * Common functions used in TripEdit.vue and TripView.vue
 */
import { addHours } from 'date-fns';
import Mapbox from 'mapbox-gl';
import HmApi from '@/api/herningMobilitetApi';
import { getPlaceVejnavn } from '@/util';
import { tripType } from '@/util/otp';
import {
  layerSources,
  // layers, loadLayers
} from '@/mapUtil/layers';

export function loadTrip(id) {
  HmApi.getTrip(id).then(trip => {
    if (trip) {
      this.$store.commit('setTrip', trip);
      this.$store.commit('setTripUd', trip.ud);
      if (trip.retur) {
        this.returTripActive = true;
        this.$store.commit('setTripRetur', trip.retur);
      } else {
        this.$store.commit('setTripRetur', {
          tripType: tripType.standard,
          fromPlace: undefined,
          toPlace: undefined,
          viaPlace: undefined,
          selectedItineraryIndex: 0,
          startTime: addHours(new Date(), 1).toISOString(),
          transportModes: {
            bike: true,
            transit: true,
          },
        });
      }

      this.setFromPlace(trip.ud.fromPlace.point);
      this.setToPlace(trip.ud.toPlace.point);
    }
  });
}

export function getItineraryModes(itinerary) {
  return [...new Set(itinerary.legs.map(leg => leg.mode))];
}
export function saveTrip() {
  const { trip } = this.state;
  trip.title = getPlaceVejnavn(trip.ud.toPlace);

  if (trip.id) {
    HmApi.updateTrip(trip.id, trip).then(resp => {});
  } else {
    HmApi.createTrip(trip, this.user).then(id => {
      trip.id = id;
    });
  }
  this.showSaveModal = true;
}

export function toggleUdSelected(udSelected) {
  this.udSelected = udSelected;
  // Replace markers:
  if (this.toPlaceMarker) {
    this.toPlaceMarker.remove();
  }
  if (this.fromPlaceMarker) {
    this.fromPlaceMarker.remove();
  }
  if (this.fromPlace) {
    this.fromPlaceMarker = this.getMarker(this.fromPlace.point, 'A').addTo(
      this.map
    );
  }
  if (this.toPlace) {
    this.toPlaceMarker = this.getMarker(this.toPlace.point, 'B').addTo(
      this.map
    );
  }

  // Remove itineraries if we don't have enough to create a new one
  if (!this.fromPlace || !this.toPlace) {
    this.$store.commit('removeItineraries');
    if (this.itinerary) {
      this.itinerary = null;
    }
    this.map.getSource(layerSources.itinerary).setData({
      type: 'FeatureCollection',
      features: [],
    });
    this.map.getSource(layerSources.advarsel).setData({
      type: 'FeatureCollection',
      features: [],
    });
    this.map.getSource(layerSources.cykelsti).setData({
      type: 'FeatureCollection',
      features: [],
    });
    this.map.getSource(layerSources.busStops).setData({
      type: 'FeatureCollection',
      features: [],
    });
    this.map.getSource(layerSources.legChange).setData({
      type: 'FeatureCollection',
      features: [],
    });
  } else {
    // Get itineraries if we have enough info
    this.getItineraries();
  }
}

export function getMarker(lngLat, text) {
  let el = document.createElement('div');
  el.className = 'marker';
  el.innerText = text;
  return new Mapbox.Marker(el).setLngLat(lngLat);
}

export function selectItinerary(index) {
  this.$store.commit('setSelectedItineraryIndex', {
    selectedItineraryIndex: index,
    udSelected: this.udSelected,
  });
}

export function onUserIconClick() {
  this.$router.push('/');
}
export function getItineraries() {
  const itineraryParams = this.udSelected
    ? this.state.trip.ud
    : this.state.trip.retur;
  if (!itineraryParams) {
    this.resetItinerary();
    return Promise.resolve();
  }
  const { fromPlace, toPlace, transportModes, viaPlace } = itineraryParams;
  if (fromPlace && toPlace) {
    this.loading = true;
    this.$toasted.clear();
    this.$toasted.global.route_loading();
    if (this.tripType === tripType.bicycleToBusStop) {
      return this.$store
        .dispatch('getItinerariesWithCustomCycleRoute', {
          from: fromPlace.point,
          to: toPlace.point,
          cycleTo: viaPlace,
          walkReluctance: this.walkReluctance,
          udSelected: this.udSelected,
          selectedItineraryIndex: this.selectedItineraryIndex,
          startTime: this.startTime,
        })
        .then(() => {
          this.$toasted.clear();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.clearItineraries();
          this.$toasted.clear();
          this.$toasted.global.route_error();
        });
    } else if (this.tripType === tripType.bicycleHome) {
      return this.$store
        .dispatch('getItinerariesWithCustomCycleRouteAtEnd', {
          from: fromPlace.point,
          to: toPlace.point,
          startTime: this.startTime,
          cycleFrom: viaPlace,
          walkReluctance: this.walkReluctance,
          udSelected: this.udSelected,
          selectedItineraryIndex: this.selectedItineraryIndex,
        })
        .then(() => {
          this.$toasted.clear();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.clearItineraries();
          this.$toasted.clear();
          this.$toasted.global.route_error();
        });
    } else {
      return this.$store
        .dispatch('getItineraries', {
          from: fromPlace.point,
          to: toPlace.point,
          startTime: this.startTime,
          transportModes,
          walkReluctance: this.walkReluctance,
          udSelected: this.udSelected,
          selectedItineraryIndex: this.selectedItineraryIndex,
        })
        .then(() => {
          this.$toasted.clear();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.clearItineraries();
          this.$toasted.clear();
          this.$toasted.global.route_error();
        });
    }
  }
}
export function setFromPlace(lngLat) {
  if (this.fromPlaceMarker) {
    this.fromPlaceMarker.remove();
  }
  this.fromPlaceMarker = this.getMarker(lngLat, 'A').addTo(this.map);
  this.editMode = undefined;
  return this.$store.dispatch('getFromPlace', {
    ...lngLat,
    udSelected: this.udSelected,
  });
}
export function setToPlace(lngLat) {
  if (this.toPlaceMarker) {
    this.toPlaceMarker.remove();
  }
  this.toPlaceMarker = this.getMarker(lngLat, 'B').addTo(this.map);
  this.editMode = undefined;
  return this.$store.dispatch('getToPlace', {
    ...lngLat,
    udSelected: this.udSelected,
  });
}

export function getFarligKrydsPopupHtml(feature) {
  const { uheldssted } = feature.properties;
  return `
    <div class="farlig-kryds-popup-container">
      Vær særlig opmærksom i kryds mellem ${uheldssted.replace('/', ' og ')}
    </div>
  `;
}
export function getCykelstiPopupHtml(feature) {
  // console.log(feature.properties);
  // const { highway, foot, segregated, surface } = feature.properties;
  // return `
  //   <div class="cykelsti-popup-container">
  //     <h1>Cykelsti/cykelbane</h1>
  //     Highway: ${highway}</br>
  //     Foot: ${foot}</br>
  //     Segregated: ${segregated}</br>
  //     Surface: ${surface}</br>
  //   </div>
  // `;
  return `
    <div class="cykelsti-popup-container">
      <h1>Cykelsti/cykelbane</h1>
    </div>
  `;
}

export function setMapViewSelected(selected) {
  this.mapViewSelected = selected;
}

/**
 * Get a readable string from input number in seconds.
 * Output examples:
 * - "2 t 32 min"
 * - "32 min"
 *
 * If less than one minute, an empty string will be
 * returned.
 * @param {number} duration Duration, in seconds
 * @returns {string} readable duration string
 */
export function getDurationString(duration) {
  let durationString = '';
  if (duration > 3600) {
    durationString += parseInt(duration / 3600) + ' t ';
    duration = duration % 3600;
  }
  if (duration > 60) {
    durationString += parseInt(duration / 60) + ' min';
  }
  return durationString;
}

export function clearItineraries() {
  this.$store.commit('removeItineraries');
  this.$store.commit('setLoadingItinerary', true);
  this.map.getSource(layerSources.itinerary).setData({
    type: 'FeatureCollection',
    features: [],
  });
  this.map.getSource(layerSources.advarsel).setData({
    type: 'FeatureCollection',
    features: [],
  });
  this.map.getSource(layerSources.cykelsti).setData({
    type: 'FeatureCollection',
    features: [],
  });
  this.map.getSource(layerSources.busStops).setData({
    type: 'FeatureCollection',
    features: [],
  });
  this.map.getSource(layerSources.legChange).setData({
    type: 'FeatureCollection',
    features: [],
  });
}
export function resetItinerary(payload) {
  if (!payload) {
    payload = {};
  }
  const { resetReturTrip = false } = payload;
  if (resetReturTrip) {
    // Dersom det kun er retur der skal fjernes (typisk ved at toggle væk retur aktiv)
    this.$store.commit('setTripRetur', undefined);
  } else {
    this.$store.commit('setTrip', {
      id: undefined,
      title: '',
      lastEdit: new Date().toISOString(),
      ud: {
        tripType: tripType.standard,
        fromPlace: undefined,
        toPlace: undefined,
        viaPlace: undefined,
        selectedItineraryIndex: 0,
        startTime: addHours(new Date(), 1).toISOString(),
        transportModes: {
          bike: false,
          transit: true,
        },
        itineraryModes: [],
      },
      retur: undefined,
    });
  }

  this.clearItineraries();
  this.error = '';
}

/**
 * When using "BICYCLE_PARK,TRANSIT" all returned itineraries from OTP
 * starts right away no matter when the first transit leg starts.
 * This is probably a but in OTP, tracked in #71 for this project.
 *
 * This function takes a list of itineraries and adjusts the times for
 * leading bicycle and walk legs, using the optional waitTime parameter.
 *
 * @param {array} itineraries
 * @param {number} [waitTime = 0] wait time before first transit leg in seconds
 * @returns {array} itineraries with leading bicycle and walk legs adjusted
 */
export function fixBicycleParkItineraries(itineraries, waitTime = 600) {
  waitTime = waitTime * 1000; // times in legs are in milliseconds
  itineraries.forEach((i, index) => {
    // Figure out where the first transit leg is
    const firstTransitLegIndex = i.legs.findIndex(
      l => l.mode === 'RAIL' || l.mode === 'BUS'
    );
    // If itinerary starts with a transit leg, we do nothing
    if (firstTransitLegIndex > 0) {
      const currentWaitTime =
        i.legs[firstTransitLegIndex].startTime -
        i.legs[firstTransitLegIndex - 1].endTime;
      const adjustedWaitTime = currentWaitTime - waitTime;

      // Fix starttime and duration for itinerary
      i = {
        ...i,
        startTime: i.startTime + adjustedWaitTime,
        duration: i.duration - adjustedWaitTime / 1000,
      };
      // Fix start- and endtime for legs
      for (var j = 0; j < firstTransitLegIndex; j++) {
        const { startTime, endTime, to } = i.legs[j];
        i.legs[j] = {
          ...i.legs[j],
          startTime: startTime + adjustedWaitTime,
          endTime: endTime + adjustedWaitTime,
          to: { ...to, arrival: to.arrival + adjustedWaitTime },
        };
      }
      itineraries[index] = i;
    }
  });
  return itineraries;
}

/**
 * Traverses itineraries -> legs -> steps and adds empty array of farligeKryds.
 * This is to ensure reactivity in Vue when adding.
 *
 * @param {array} itineraries
 * @returns {array} itineraries with empty farligKryds-array added to each step of each leg
 */
export function decorateStepsWithFarligeKryds(itineraries) {
  return itineraries.map(i => {
    return {
      ...i,
      legs: i.legs.map(l => {
        return {
          ...l,
          steps: l.steps.map(s => {
            return { ...s, farligeKryds: [] };
          }),
        };
      }),
    };
  });
}
